import React, { useState, useEffect, useMemo, useRef } from "react";
import { TreeSelect } from "antd";
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import { api, queryBuilder } from "services";
import cx from "classnames";
import "./style.scss"

const PAGE_SIZE = 15;

const buildTree = (items) => {
  const map = {};
  const roots = [];

  items.forEach(item => {
    map[item.id] = {
      title: item.name,
      value: item.id,
      parent_id: item.parent_id,
      children: [],
      selectable: false
    };
  });

  items.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push({
        ...map[item.id],
        selectable: true
      });
    }
  });

  items.forEach(item => {
    if (!item.parent_id) {
      if (map[item.id].children.length === 0) {
        map[item.id].selectable = true;
      }
      roots.push(map[item.id]);
    }
  });

  return roots;
};

const normalizeValueToIds = (val) => {
  if (val == null || val === "") return [];
  const arr = Array.isArray(val) ? val : [val];
  return arr
    .map((v) => {
      if (v !== null && typeof v === "object" && "id" in v) return v.id;
      return v;
    })
    .filter((v) => v !== null && v !== undefined && v !== "");
};


const AsyncNestedSelectD1 = ({
  placeholder,
  label,
  isRequired,
  loadOptionsUrl,
  loadOptionsParams = () => ({}),
  isClearable,
  isDisabled,
  size = "default",
  isMulti = false,
  handleChange = () => { },
  filterParams = {},
  value,
  className = "",
  refetch,
  refetchData,
}) => {
  const [visible, setVisible] = useState(true);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allLoadedItems, setAllLoadedItems] = useState([]);
  const allLoadedItemsRef = useRef([]);

  useEffect(() => {
    allLoadedItemsRef.current = allLoadedItems;
  }, [allLoadedItems]);

  useEffect(() => {
    setVisible(false);
    setTimeout(() => setVisible(true), 0);
  }, [refetch, refetchData]);

  useEffect(() => {
    loadData("", 1, false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const ids = normalizeValueToIds(value);
      if (!ids.length || !loadOptionsUrl) return;
      const loaded = allLoadedItemsRef.current;
      const missing = ids.filter(
        (id) => !loaded.some((i) => String(i.id) === String(id))
      );
      if (!missing.length) return;
      try {
        setLoading(true);
        const res = await api.request.get(
          queryBuilder(loadOptionsUrl, {
            filter: { id: missing },
            include: "children",
            sort: "name",
            limit: 100,
          })
        );
        if (cancelled) return;
        const list = get(res, "data.data", []) || [];
        if (!Array.isArray(list) || !list.length) return;
        setAllLoadedItems((prev) => {
          const merged = [...prev];
          list.forEach((i) => {
            if (!merged.some((m) => m.id === i.id)) merged.push(i);
          });
          setTreeData(buildTree(merged));
          return merged;
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [value, loadOptionsUrl]);

  const loadData = async (search = "", page = 1, isSearchMode = false) => {
    if (!isSearchMode && page !== 1 && !hasMore) return;

    setLoading(true);

    const params = {
      ...filterParams,
      ...loadOptionsParams(search),
    };

    if (!isSearchMode) {
      params.per_page = PAGE_SIZE;
      params.page = page;
    }

    try {
      const res = await api.request.get(
        queryBuilder(loadOptionsUrl, params)
      );

      const data = get(res, "data", {});
      const items = data.data || [];
      const lastPage = data.last_page;
      const currentPageFromResponse = data.current_page || 1;

      let newLoadedItems;
      if (isSearchMode || page === 1) {
        newLoadedItems = items;
        if (isSearchMode) {
          setAllLoadedItems(items);
          setTreeData(
            newLoadedItems.map(i => ({
              title: i.name,
              value: i.id,
              selectable: true,
            }))
          );
        } else {
          setAllLoadedItems(prev => {
            const merged = [...prev];
            items.forEach(i => {
              if (!merged.some(m => m.id === i.id)) merged.push(i);
            });
            setTreeData(buildTree(merged));
            return merged;
          });
        }
      } else {
        newLoadedItems = [...allLoadedItems, ...items];
        setAllLoadedItems(newLoadedItems);
        setTreeData(buildTree(newLoadedItems));
      }

      if (!isSearchMode) {
        setHasMore(currentPageFromResponse < lastPage);
        setCurrentPage(currentPageFromResponse);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Ma'lumotlarni yuklashda xatolik:", error);
      if (page === 1) {
        setTreeData([]);
        setAllLoadedItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const debouncedLoadData = useMemo(
    () =>
      debounce((search) => {
        if (search) {
          loadData(search, 1, true);
        } else {
          loadData("", 1, false);
        }
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedLoadData.cancel();
    };
  }, [debouncedLoadData]);


  const fetchNextPage = () => {
    if (!loading && hasMore && !searchValue) {
      loadData(searchValue, currentPage + 1, false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      fetchNextPage();
    }
  };

  return (
    <div className={cx("form-field", className)}>
      {label && (
        <label className="form-field__label">
          {label}
          {isRequired && <span title={"Obyazatelnoe pole"}>*</span>}
        </label>
      )}
      {visible ? (
        <TreeSelect
          showSearch
          value={value}
          treeData={treeData}
          filterTreeNode={false}
          placeholder={placeholder}
          allowClear={isClearable}
          disabled={isDisabled}
          label={label}
          className={`custom-treeselectd ${className}`}
          treeDefaultExpandAll={false}
          onSearch={(val) => {
            setSearchValue(val);
            debouncedLoadData(val);
          }}
          onDropdownVisibleChange={(open) => {
            if (!open && searchValue) {
              setSearchValue("");
              loadData("", 1, false);
            }
          }}
          multiple={isMulti}
          loading={loading}
          onChange={(val, vals, extra) => {
            if (!extra) {
              handleChange([], [], []);
              return;
            }
            if (isMulti) {
              const nodes = get(extra, "allCheckedNodes")
                ? get(extra, "allCheckedNodes", []).map(n => get(n, "node.props"))
                : [];
              handleChange(val, vals, nodes);
            } else {
              handleChange(val, vals, get(extra, "triggerNode.props", null));
            }
          }}

          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          dropdownRender={menu => (
            <div onScroll={handleScroll}>
              {menu}
              {loading && (
                <div style={{ textAlign: 'center', padding: '5px' }}>
                  Yuklanmoqda...
                </div>
              )}
              {!hasMore && !searchValue && allLoadedItems.length > 0 && (
                <div style={{ textAlign: 'center', padding: '5px', color: '#999' }}>
                  Barcha ma'lumotlar yuklandi.
                </div>
              )}
            </div>
          )}
          maxTagCount={"responsive"}
          maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length}`}
          getPopupContainer={triggerNode => get(triggerNode, "parentNode")}
        />
      ) : (
        <div style={{ height: size === "small" ? 35 : 48 }} />
      )}
    </div>
  );
};

export default AsyncNestedSelectD1;
